import _ from 'lodash';
import { IhmSpyglassServiceClient } from '../services/coralservices/serviceClients/IhmSpyglassServiceClient';
import { Exception } from '../types/Exception';
import { ItemIdWithTypeUtils, InvalidAsinFcSkuException, ItemIdType } from './ItemIdWithType';
import { ProductInfo } from './ProductInfo';

interface ISearchOptions {
    marketplaceId?: string;
    merchantId?: string;
    excludeProductsWithoutCategory?: boolean;
    searchIndex?: string;
    searchAlias?: string;
    asins?: string[];
}

export const ProductSearchConstants = {
    MAX_NUMBER_OF_RESULTS: 20,
    DEFAULT_EXCLUDE_PRODUCTS_WITHOUT_CATEGORY: false,
    DE_AMAZON_MARKETPLACE_ID: 'A1PA6795UKMFR9',
    DE_AMAZON_MERCHANT_ID: 'A3JWKAKR8XB7XF',
    UK_AMAZON_MARKETPLACE_ID: 'A1F83G8C2ARO7P',
    UK_AMAZON_MERCHANT_ID: 'A3P5ROKL5A1OLE',
    US_AMAZON_MARKETPLACE_ID: 'ATVPDKIKX0DER',
    US_AMAZON_MERCHANT_ID: 'ATVPDKIKX0DER',
    SEARCH_INDEX: {
        MARKETPLACE: 'marketplace',
        DE_BOOKS: 'eu-books-de',
        UK_BOOKS: 'eu-books-uk',
        US_BOOKS: 'na-books-us',
    },
    SEARCH_ALIAS: {
        MARKETPLACE: 'generic-marketplace',
        BOOKS: 'books-single-index',
    },
};

export const ProductSearchKeywordType = Object.freeze({
    ALL: 'ALL',
    TITLE: 'TITLE',
    AUTHOR: 'AUTHOR',
    UPC: 'UPC',
    ISBN: 'ISBN',
});

export class ProductSearchHelper {
    public static allKeywordTypes = ProductSearchKeywordType;
    public static allSearchIndexes = ProductSearchConstants.SEARCH_INDEX;
    public static searchTermPattern = new RegExp("^[[A-z0-9\\?\\-\\.\\:\\'\\!\\(\\)\\# ]{3,50}$");
    public static searchProductsByKeyword(storeId: string, searchKeywordType, searchTerm, maxNumberOfResults?: any, searchOptions?: ISearchOptions) {
        maxNumberOfResults = maxNumberOfResults || ProductSearchConstants.MAX_NUMBER_OF_RESULTS;
        let searchIndex: any = ProductSearchConstants.SEARCH_INDEX.MARKETPLACE;
        let searchAlias: any = ProductSearchConstants.SEARCH_ALIAS.MARKETPLACE;
        let excludeProductsWithoutCategory: any = ProductSearchConstants.DEFAULT_EXCLUDE_PRODUCTS_WITHOUT_CATEGORY;
        let marketplaceId: any;
        let merchantId: any;
        let asins: any;
        if (_.isObject(searchOptions)) {
            marketplaceId = searchOptions.marketplaceId;
            merchantId = searchOptions.merchantId;
            excludeProductsWithoutCategory = searchOptions.excludeProductsWithoutCategory || ProductSearchConstants.DEFAULT_EXCLUDE_PRODUCTS_WITHOUT_CATEGORY;
            searchIndex = searchOptions.searchIndex || ProductSearchConstants.SEARCH_INDEX.MARKETPLACE;
            searchAlias = searchOptions.searchAlias || ProductSearchConstants.SEARCH_ALIAS.MARKETPLACE;
            asins = searchOptions.asins;
        }

        const validationPromises = [
            ProductSearchHelper.validateSearchKeywordType(ProductSearchHelper.allKeywordTypes, searchKeywordType),
            ProductSearchHelper.validateSearchTerm(searchTerm),
            ProductSearchHelper.validateMaxNumberOfResults(maxNumberOfResults),
            ProductSearchHelper.validateSearchIndex(ProductSearchHelper.allSearchIndexes, searchIndex),
            ProductSearchHelper.validateMarketplaceId(marketplaceId, merchantId),
        ];

        return Promise.all(validationPromises).then((Exceptions) => {
            return IhmSpyglassServiceClient.searchProductsByKeyword(
                storeId,
                searchKeywordType,
                searchTerm,
                maxNumberOfResults,
                excludeProductsWithoutCategory,
                searchIndex,
                searchAlias,
                marketplaceId,
                merchantId,
                asins,
            );
        });
    }

    public static validateSearchKeywordType(allKeywordTypes, searchKeywordType) {
        const typeExists = _.includes(allKeywordTypes, searchKeywordType);
        if (!typeExists) {
            return Promise.reject(
                new Exception({
                    message: 'The search keyword type must be one of the following: ' + allKeywordTypes,
                }),
            );
        } else {
            return Promise.resolve();
        }
    }

    public static validateSearchTerm(searchTerm) {
        if (searchTerm.length < 3 || searchTerm.length > 50) {
            return Promise.reject(new Exception({ message: 'The search term must be between 3 and 50 characters in length.' }));
        } else if (!ProductSearchHelper.searchTermPattern.test(searchTerm)) {
            return Promise.reject(new Exception({ message: 'Unable to find product.' }));
        } else {
            return Promise.resolve();
        }
    }

    public static validateMaxNumberOfResults(maxNumberOfResults) {
        if (maxNumberOfResults < 1 || maxNumberOfResults > 100) {
            return Promise.reject(new Exception({ message: 'The max number of results requested must be between 1 and 100.' }));
        } else {
            return Promise.resolve();
        }
    }

    public static validateSearchIndex(allSearchIndexes, searchIndex) {
        const indexExists = _.includes(allSearchIndexes, searchIndex);
        if (!indexExists) {
            return Promise.reject(new Exception({ message: 'The search index must be one of the following: ' + allSearchIndexes }));
        } else {
            return Promise.resolve();
        }
    }

    public static validateMarketplaceId(marketplaceId?, merchantId?) {
        if (marketplaceId && !merchantId) {
            return Promise.reject(new Exception({ message: 'MerchantId have to be defined when using a custom merchantId.' }));
        } else {
            return Promise.resolve();
        }
    }

    /**
     * Returns the ASINs for the item search term. If the search term is of a
     * whitelisted pattern (UPC, PLU etc), this uses getProductInfo to retrieve
     * the ASIN information. If the search term does not follow a whitelisted
     * pattern, or if the search term resolves to a ASIN/FCSKU pattern, we fall
     * back to searchProductsByKeyword (RSAS).
     *
     * If the searchTerm is of type GS1-128, for the time being we will bypass
     * product validation.
     */

    public static async getAsinsForProduct(storeId, searchKeywordType, searchTerm, maxNumberOfResults?, searchOptions?) {
        try {
            return ItemIdWithTypeUtils.convertToItemIdWithType(searchTerm, storeId).then((itemIdWithType) => {
                if (itemIdWithType.idType === ItemIdType.GS1) {
                    // GS1 code only has GTIN.  GTINs require translation to ASIN before we can fetch product info
                    // We'll need a new solution as validation cannot be done in parallel to ASIN translation
                    return { asins: [''] };
                }

                if (itemIdWithType.idType === ItemIdType.UNKNOWN) {
                    return ProductSearchHelper.searchProductsByKeyword(storeId, searchKeywordType, searchTerm, maxNumberOfResults, searchOptions);
                } else if (itemIdWithType.idType === ItemIdType.ASIN) {
                    return { asins: [itemIdWithType.itemId] };
                } else {
                    return ProductInfo.getProductInfoFacets(storeId, itemIdWithType).then((facets) => {
                        return { asins: [facets.asin] };
                    });
                }
            });
        } catch (exception) {
            if (exception instanceof InvalidAsinFcSkuException) {
                return ProductSearchHelper.searchProductsByKeyword(storeId, searchKeywordType, searchTerm, maxNumberOfResults);
            } else {
                return { asins: [] };
            }
        }
    }
    public searchProductsByKeyword: unknown;
    public getAsinsForProduct: unknown;
}
